import { NgFor, NgIf } from '@angular/common'
import { Component, DestroyRef, inject, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { BadgeEditorComponent } from '@app/modules/admin/components/badge-editor/badge-editor.component'
import { AdminEventCardComponent } from '@app/modules/admin/components/shared/admin-event-card/admin-event-card.component'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { ServerRepresentedEvent } from '@app/modules/events/models/event.model'
import { EventService } from '@app/modules/events/services/event.service'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { ToastrService } from '@app/services/toastr.service'
import { InfiniteScrollModule } from 'ngx-infinite-scroll'
import { EMPTY, merge, Observable, of, Subject } from 'rxjs'
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, takeUntil } from 'rxjs/operators'
import { GroupFormsComponent } from '../group-forms/group-forms.component'
import { OptionsComponent } from '../options/options.component'

export type EventToolKind = 'badge' | 'group-forms' | 'settings'

export interface EventWithForms extends Partial<ServerRepresentedEvent> {
  detailedForms?: Persona[]
}

interface EventListResult {
  response: { data: ServerRepresentedEvent[]; current_page?: number; last_page?: number }
  page: number
  errorMessage: string
}

@Component({
  selector: 'ngx-event-tools-hub',
  standalone: true,
  templateUrl: './event-tools-hub.component.html',
  styleUrls: ['./event-tools-hub.component.scss'],
  imports: [NgIf, NgFor, FormsModule, InfiniteScrollModule, AdminEventCardComponent, BadgeEditorComponent, GroupFormsComponent, OptionsComponent],
  providers: [EventService]
})
export class EventToolsHubComponent implements OnInit {
  public events: EventWithForms[] = []
  public searchTerm = ''
  public isLoadingEvents = false
  public isLoadingMore = false
  public isPreparingTool = false
  public hasMoreEvents = true
  public eventsErrorMessage = ''
  public hasWorkspaceError = false

  public activeTool: EventToolKind | null = null
  public selectedEvent: EventWithForms | null = null

  private _page = 1
  private readonly _perPage = 10
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _destroy$ = new Subject<void>()
  private readonly _searchSubject = new Subject<string>()
  private readonly _listLoad$ = new Subject<boolean>()

  constructor(
    private readonly _router: Router,
    private readonly _authService: AuthService,
    private readonly _eventService: EventService,
    private readonly _toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (!this._authService.user?.is_admin) {
      this._router.navigate(['/'])
      return
    }

    if (!this._authService.currentWorkspace) {
      this._router.navigate(['/workspaces'])
      return
    }

    merge(
      this._searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        map((term) => {
          this.searchTerm = term
          return true
        })
      ),
      this._listLoad$
    )
      .pipe(
        switchMap((reset) => this._fetchEvents(reset)),
        takeUntil(this._destroy$)
      )
      .subscribe((result) => this._applyEvents(result))

    this._destroyRef.onDestroy(() => {
      this._destroy$.next()
      this._destroy$.complete()
    })

    this._loadEvents()
  }

  public get showEventList(): boolean {
    return !this.activeTool
  }

  public onSearchTermChanged(event: Event): void {
    const target = event.target as HTMLInputElement
    this._searchSubject.next(target.value)
  }

  public clearSearch(): void {
    this.searchTerm = ''
    this._searchSubject.next('')
  }

  public loadMore(): void {
    if (this.isLoadingEvents || this.isLoadingMore || !this.hasMoreEvents) return
    this._loadEvents(false)
  }

  public openBadge(event: ServerRepresentedEvent): void {
    this._openTool('badge', event)
  }

  public openGroupForms(event: ServerRepresentedEvent): void {
    this._openTool('group-forms', event)
  }

  public openSettings(event: ServerRepresentedEvent): void {
    this._openTool('settings', event)
  }

  public backToEventList(): void {
    this.activeTool = null
    this.selectedEvent = null
    this.isPreparingTool = false
  }

  private _openTool(tool: EventToolKind, event: ServerRepresentedEvent): void {
    if (!event?.id) {
      this._toastr.error('This event has no ID', 'Error')
      return
    }

    this.isPreparingTool = true
    this._eventService
      .getEventForms(event.id)
      .pipe(
        takeUntil(this._destroy$),
        catchError(() => {
          this._toastr.error('Failed to load event forms', 'Error')
          return of([] as Persona[])
        }),
        finalize(() => {
          this.isPreparingTool = false
        })
      )
      .subscribe((forms) => {
        this.selectedEvent = {
          ...event,
          detailedForms: forms || [],
          forms: forms || event.forms || []
        }
        this.activeTool = tool
      })
  }

  private _loadEvents(reset = true): void {
    this._listLoad$.next(reset)
  }

  private _fetchEvents(reset: boolean): Observable<EventListResult> {
    if (!this._authService.currentWorkspace?.id) {
      this.hasWorkspaceError = true
      this.eventsErrorMessage = 'Workspace context lost. Please refresh the page.'
      this.hasMoreEvents = false
      this.isLoadingEvents = false
      this.isLoadingMore = false
      return EMPTY
    }

    if (reset) {
      this._page = 1
      this.hasMoreEvents = true
      this.events = []
      this.isLoadingEvents = true
      this.isLoadingMore = false
    } else {
      this.isLoadingMore = true
    }

    this.hasWorkspaceError = false
    this.eventsErrorMessage = ''
    const page = this._page

    return this._eventService.getEvents(page, this.searchTerm, 0, false, this._perPage, 1).pipe(
      map((response) => ({ response, page, errorMessage: '' })),
      catchError((error) =>
        of({
          response: { data: [] as ServerRepresentedEvent[], current_page: page, last_page: page },
          page,
          errorMessage: `Failed to load events: ${error.error?.message || error.message || 'Unknown error'}`
        })
      )
    )
  }

  private _applyEvents(result: EventListResult): void {
    this.isLoadingEvents = false
    this.isLoadingMore = false

    if (result.errorMessage) {
      this.hasMoreEvents = false
      this.eventsErrorMessage = result.errorMessage
    }

    const visibleEvents = (result.response?.data || [])
      .filter((event) => {
        if (!event?.public_start_date || !event?.public_end_date) return false
        if (new Date() > new Date(event.public_end_date)) return false
        return !!event.forms?.length
      })
      .sort((a, b) => this._compareEvents(a, b))

    this.events = result.page === 1 ? visibleEvents : [...this.events, ...visibleEvents]
    const currentPage = result.response?.current_page ?? result.page
    this.hasMoreEvents = typeof result.response?.last_page === 'number' ? currentPage < result.response.last_page : (result.response?.data?.length || 0) >= this._perPage
    this._page = currentPage + 1
  }

  private _compareEvents(a: EventWithForms, b: EventWithForms): number {
    const statusRank = (event: EventWithForms): number => {
      if (!event.public_start_date || !event.public_end_date) {
        return 3
      }
      const now = new Date()
      const start = new Date(event.public_start_date)
      const end = new Date(event.public_end_date)
      if (now >= start && now <= end) {
        return 0
      }
      if (now < start) {
        return 1
      }
      return 2
    }

    const rankDiff = statusRank(a) - statusRank(b)
    if (rankDiff !== 0) {
      return rankDiff
    }
    if (a.public_start_date && b.public_start_date) {
      return new Date(a.public_start_date).getTime() - new Date(b.public_start_date).getTime()
    }
    return 0
  }
}
